import {
  Button,
  ButtonGroup,
  FlexBox,
  Table,
  Tag,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import styledComponents from "styled-components";

import { ReactComponent as ArrowForwardIcon } from "../../public/material-icons/arrow-forward.svg";
import { ReactComponent as EditIcon } from "../../public/material-icons/edit.svg";
import { updateRoomMeta } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import { useAllDevices } from "../hooks/use-devices";
import {
  resolveIncidentDevice,
  useActiveIncidents,
} from "../hooks/use-incidents";
import { useActiveRoom, useAllRooms } from "../hooks/use-rooms";
import type { Room } from "../view-model";
import { RoomEditModal } from "./room-edit-modal";

// The room list formats contact as "email: <email>\n<details>" — reverse that
// back into the structured { email, details } the rooms API expects.
function parseContactData(blob?: string): { email?: string; details?: string } {
  if (!blob) return {};
  const lines = blob.split("\n");
  const emailLine = lines.find((line) =>
    line.trim().toLowerCase().startsWith("email:")
  );
  const email = emailLine?.replace(/^\s*email:\s*/i, "").trim() || undefined;
  const details =
    lines
      .filter((line) => line !== emailLine)
      .join("\n")
      .trim() || undefined;
  return { email, details };
}

const StyledInfo = styledComponents.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledEditButton = styledComponents(Button)`
  padding: 12px;
  background: transparent;
`;

const LoadingSkeletons = Array.from({ length: 3 }).map((_element, index) => (
  // eslint-disable-next-line react/no-array-index-key
  <Table.Row key={index}>
    <Table.Data>
      <Skeleton count={2} />
    </Table.Data>
    <Table.Data>
      <Skeleton />
    </Table.Data>
    <Table.Data>
      <Skeleton />
    </Table.Data>
    <Table.Data>
      <Skeleton />
    </Table.Data>
    <Table.Data>
      <Skeleton />
    </Table.Data>
    <Table.Data>
      <Skeleton count={2} />
    </Table.Data>
  </Table.Row>
));

export type RoomSwitchParams = {
  rooms?: Room[];
  isLoading: boolean;
};

export const RoomSwitch = ({ isLoading, rooms }: RoomSwitchParams) => {
  const { t } = useTranslation();
  const token = useAccessToken();
  const [searchParams] = useSearchParams();
  const envRoomId = import.meta.env.VITE_ROOM_ID;
  const defaultRoomId = searchParams.get("defaultRoom") || envRoomId;
  const { activeRoom, setActiveRoom } = useActiveRoom();
  const { mutate, rooms: allRooms } = useAllRooms();
  const [filter, setFilter] = useState<"all" | "errors">("all");
  const [roomsWithErrors, setRoomsWithErrors] = useState(rooms);
  const { incidents } = useActiveIncidents();
  const { devices } = useAllDevices();
  const navigate = useNavigate();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [roomToEdit, setRoomToEdit] = useState<Room | null>(null);

  useEffect(() => {
    if (rooms && incidents) {
      // Mount-aware: an incident is canonical under its SOURCE device. Resolve
      // each critical one to the device that OWNS it (a monitored device shows
      // its mounted sources) so a room with only monitored-device errors is
      // still flagged — a naive deviceId match would miss it.
      const criticalOwnerIds = new Set(
        incidents
          .filter((incident) => incident.severity === "critical")
          .map((incident) => resolveIncidentDevice(incident, devices ?? [])?.id)
          .filter(Boolean)
      );
      const roomsWithCriticalIncidents = rooms.filter((room) =>
        (room.deviceIds ?? []).some((deviceId) =>
          criticalOwnerIds.has(deviceId)
        )
      );
      setRoomsWithErrors(roomsWithCriticalIncidents);
    }
  }, [filter, incidents, rooms, devices]);

  const onSelectRoom = (room: Room) => {
    setActiveRoom(room);
    navigate({
      pathname: "/",
      search: defaultRoomId ? `defaultRoom=${defaultRoomId}` : "",
    });
  };

  function handleEditRoom(room: Room) {
    setRoomToEdit(room);
    setEditModalOpen(true);
  }

  function handleSaveRoom(updatedRoom: Room) {
    if (!updatedRoom) return;

    // Optimistically update the rooms list
    if (allRooms) {
      const updatedRooms = allRooms.map((room) =>
        room.id === updatedRoom.id ? updatedRoom : room
      );
      mutate(updatedRooms, false); // Update UI immediately without revalidation
    }

    const meta = {
      name: updatedRoom.name,
      number: updatedRoom.roomNumber,
      contact: {
        name: updatedRoom.contactPerson,
        ...parseContactData(updatedRoom.contactData),
      },
    };

    updateRoomMeta(updatedRoom.id.toString(), meta, token ?? undefined)
      .then(() => {
        setEditModalOpen(false);
        setRoomToEdit(null);
        mutate();
      })
      .catch(() => {
        setEditModalOpen(false);
        mutate();
      });
  }

  return (
    <FlexBox alignItems="end" flexDirection="column">
      <ButtonGroup isCompact>
        <Button
          onClick={() => setFilter("all")}
          variant={filter === "all" ? "primary" : "secondary"}
        >
          {t("room.all")}
        </Button>
        <Button
          onClick={() => setFilter("errors")}
          variant={filter === "errors" ? "primary" : "secondary"}
        >
          {t("room.error")}
        </Button>
      </ButtonGroup>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Header />
            <Table.Header>{t(`page.rooms`)}</Table.Header>
            <Table.Header>{t(`header.status`)}</Table.Header>
            <Table.Header />
            <Table.Header>{t(`header.contactperson`)}</Table.Header>
            <Table.Header>{t(`header.contact`)}</Table.Header>
            <Table.Header />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {isLoading && LoadingSkeletons}
          {(filter === "all" ? rooms : roomsWithErrors)?.map((room) => (
            <Table.Row
              active={activeRoom?.id === room.id}
              hasBorder
              key={room.id}
              onClick={() => onSelectRoom(room)}
            >
              <Table.Data>
                <StyledEditButton
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore - We need the event to stop propagation
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditRoom(room);
                  }}
                  variant="secondary"
                >
                  <EditIcon />
                </StyledEditButton>
              </Table.Data>
              <Table.Data>
                <FlexBox>
                  <StyledInfo>
                    <Typography color="black" size="sm" weight="semiBold">
                      {room.name}
                    </Typography>
                    <Typography color="grey400" size="sm">
                      {room.roomNumber || room.id}
                    </Typography>
                  </StyledInfo>
                </FlexBox>
              </Table.Data>
              <Table.Data>
                {roomsWithErrors?.includes(room) && (
                  <Tag variant="error">{t(`incident.type.critical`)}</Tag>
                )}
              </Table.Data>
              <Table.Data>
                {room.id.toString() === defaultRoomId && (
                  <Tag variant="info">{t(`room.default`)}</Tag>
                )}
              </Table.Data>
              <Table.Data>{room.contactPerson}</Table.Data>
              <Table.Data>
                <Typography size="xs" whiteSpace="pre-line">
                  {room.contactData}
                </Typography>
              </Table.Data>
              <Table.Data>
                <FlexBox justifyContent="flex-end">
                  {activeRoom?.id !== room.id && (
                    <Button variant="ghost">
                      {t(`room.change`)}
                      <ArrowForwardIcon />
                    </Button>
                  )}
                </FlexBox>
              </Table.Data>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <RoomEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveRoom}
        room={roomToEdit}
      />
    </FlexBox>
  );
};
